<template>
  <div class="vehicle-tile__container">
    <wb-card class="vehicle-tile">
      <div v-if="showDistanceTag" class="vehicle-tile__tag__container">
        <div class="vehicle-tile__tag">
          <wb-icon class="vehicle-tile__tag__icon" name="bds/location/16" />
          <span class="vehicle-tile__tag__text">{{ dealerDistance }}</span>
        </div>
        <wb-tooltip-host
          v-if="showWeightTag"
          class="form-section-hint"
          position="top"
        >
          <wb-tooltip>
            {{ $t('schedule.booking.vehicleTile.weight.tooltip') }}
          </wb-tooltip>
          <div class="vehicle-tile__tag vehicle-tile__tag-license">
            <span class="vehicle-tile__tag__text">{{
              $t('schedule.booking.vehicleTile.tag.weightText')
            }}</span>
          </div>
        </wb-tooltip-host>
      </div>

      <template v-if="dealerInfoModalOpen">
        <wb-modal
          mode="edit"
          close-on-backdrop-click
          close-on-escape-key
          scroll-lock
          @wbclose="toggleDealerInfoModal"
        >
          <wb-modal-header></wb-modal-header>
          <wb-modal-content>
            <div class="schedule-modal-header">
              <widget-loader
                id="dealerWidget"
                ref="dealerWidget"
                @resources-loaded="dealerInitWidget"
                :resources="dealerSearchResources"
              />
            </div>
          </wb-modal-content>
        </wb-modal>
      </template>
      <vehicle-information
        :vehicleInformation="vehicleInformation"
        :dealerInformation="dealerInformation"
        :theme="'white'"
        :path="path"
      ></vehicle-information>
      <div
        class="vehicle-tile__dealer-info"
        v-for="item in dealerInformation"
        :key="item.key"
      >
        <div class="vehicle-tile__dealer-info__left">
          <span
            class="vehicle-tile__dealer-info__left-name wb-type-copy-strong"
            >{{ item.key }}</span
          >
          <span
            class="vehicle-tile__dealer-info__left-address wb-type-copy-secondary"
            >{{ item.value }}</span
          >
        </div>
        <div class="vehicle-tile__dealer-info__right">
          <button
            @click="toggleDealerInfoModal"
            class="vehicle-tile__dealer-info__button wb-button wb-button--tertiary wb-button--small"
          >
            {{ $t('schedule.booking.vehicleTile.moreInfo') }}
          </button>
        </div>
      </div>
      <div class="divider"></div>
      <button
        @click="proceedBooking"
        class="wb-button wb-button--medium wb-button--tertiary vehicle-tile__button"
      >
        {{ $t('schedule.booking.vehicleTile.proceedCta') }}
      </button>
    </wb-card>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { useDirection } from '../../composables/direction.composable';

// Components
import { VehicleInformation, WidgetLoader } from '../index';

// Mocks
import useDealerLocator from '../../composables/dealer-locator.composable';

import { useStore } from '../../composables/store.composable';
import {
  TRACKING_EVENT_ACTION,
  TRACKING_EVENT_CATEGORY,
  TRACKING_EVENT_LABEL,
  TRACKING_EVENT_NAME,
} from '../../models/constants';
import GenericReceivedObject from '../../models/utilities/generic-received-object.model';

import {
  CONTEXT_OPTIONS,
  WEIGHT_CATEGORY,
} from '@dh-io-otb/otbc/src/models/constants';
import TrackingService from '../../services/tracking.service';

export default defineComponent({
  name: 'otb-vehicle-information',
  components: {
    'vehicle-information': VehicleInformation,
    'widget-loader': WidgetLoader,
  },
  props: {
    vehicleInformation: {
      type: VehicleInformation,
      default: {},
    },
    motorisation: {},
    dealerInformation: {
      type: Array,
      default: Array as () => GenericReceivedObject[],
    },
    dealerId: String,
    path: String,
    weightValue: String,
  },
  setup(props, { emit }) {
    const { isRTL } = useDirection();
    const { getter, state } = useStore();
    const { dealerSearchResources, initWidget } = useDealerLocator();
    const aemFormsResources = computed((): string => getter.aemFormsResources);
    const dealerWidget = ref();
    let dealerInfoModalOpen = ref(false);

    const toggleDealerInfoModal = () => {
      if (!dealerInfoModalOpen.value) {
        const event = {
          category: TRACKING_EVENT_CATEGORY.FEATURE,
          action: TRACKING_EVENT_ACTION.VEHICLE_CARD,
          label: TRACKING_EVENT_LABEL.VEHICLE_CARD_INFO,
          name: TRACKING_EVENT_NAME.FEATURE,
        };
        TrackingService.trackEvent(event);
      }
      dealerInfoModalOpen.value = !dealerInfoModalOpen.value;
    };

    const dealerInitWidget = async () =>
      initWidget(dealerWidget.value.$el, props.dealerId);

    const dealerDistance = computed((): string => {
      const formattedDistance = props.dealerInformation[0]?.distance?.replace(
        '.',
        ','
      );
      return `${formattedDistance} ${props.dealerInformation[0]?.unit}`;
    });

    const showWeightTag = computed(() => {
      if (state.configuration.context === CONTEXT_OPTIONS.CARS) return false;
      return props.weightValue === WEIGHT_CATEGORY.HEAVY_WEIGHT;
    });

    const showDistanceTag = computed((): boolean => {
      return !state.otbcFlow.fixedDealer;
    });

    const proceedBooking = () => {
      emit('toggleDatepickerModal');
      const event = {
        category: TRACKING_EVENT_CATEGORY.FEATURE,
        action: TRACKING_EVENT_ACTION.VEHICLE_CARD,
        label: TRACKING_EVENT_LABEL.VEHICLE_CARD_BOOKING,
        name: TRACKING_EVENT_NAME.FEATURE,
      };
      TrackingService.trackEvent(event);
    };

    return {
      emit,
      isRTL,
      aemFormsResources,
      // methods
      toggleDealerInfoModal,
      initWidget,
      proceedBooking,
      // computed
      dealerSearchResources,
      showDistanceTag,
      showWeightTag,
      // refs
      dealerInfoModalOpen,
      dealerInitWidget,
      dealerWidget,
      dealerDistance,
    };
  },
});
</script>

<style scoped src="./vehicle-tile.scss" lang="scss"></style>
